import {
  Box,
  IconButton,
  Typography,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  Download as DownloadIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
} from '@mui/icons-material';
import type { PdfViewerToolbarProps } from '../../types/pdf.types';

const ZOOM_OPTIONS = [
  { value: 0.5, label: '50%' },
  { value: 0.75, label: '75%' },
  { value: 1, label: '100%' },
  { value: 1.25, label: '125%' },
  { value: 1.5, label: '150%' },
  { value: 2, label: '200%' },
];

export function PdfViewerToolbar({
  currentPage,
  numPages,
  scale,
  onPageChange,
  onScaleChange,
  onDownload,
  fileName,
}: PdfViewerToolbarProps) {
  const handlePrevPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (numPages && currentPage < numPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handleZoomIn = () => {
    const currentIndex = ZOOM_OPTIONS.findIndex((opt) => opt.value === scale);
    if (currentIndex < ZOOM_OPTIONS.length - 1) {
      onScaleChange(ZOOM_OPTIONS[currentIndex + 1].value);
    }
  };

  const handleZoomOut = () => {
    const currentIndex = ZOOM_OPTIONS.findIndex((opt) => opt.value === scale);
    if (currentIndex > 0) {
      onScaleChange(ZOOM_OPTIONS[currentIndex - 1].value);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 1,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          maxWidth: 200,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {fileName}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title="Vorherige Seite">
          <span>
            <IconButton
              size="small"
              onClick={handlePrevPage}
              disabled={currentPage <= 1}
            >
              <PrevIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Typography variant="body2">
          {currentPage} / {numPages || '-'}
        </Typography>

        <Tooltip title="Naechste Seite">
          <span>
            <IconButton
              size="small"
              onClick={handleNextPage}
              disabled={!numPages || currentPage >= numPages}
            >
              <NextIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title="Verkleinern">
          <span>
            <IconButton
              size="small"
              onClick={handleZoomOut}
              disabled={scale <= ZOOM_OPTIONS[0].value}
            >
              <ZoomOutIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Select
          size="small"
          value={scale}
          onChange={(e) => onScaleChange(e.target.value as number)}
          sx={{ minWidth: 80 }}
        >
          {ZOOM_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>

        <Tooltip title="Vergroessern">
          <span>
            <IconButton
              size="small"
              onClick={handleZoomIn}
              disabled={scale >= ZOOM_OPTIONS[ZOOM_OPTIONS.length - 1].value}
            >
              <ZoomInIcon />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Herunterladen">
          <IconButton size="small" onClick={onDownload}>
            <DownloadIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
